import React, { useContext, useEffect, useState } from 'react';
import { useCreateReturnOrderFulfillmentMutation } from '../../../graphql/mutations/createReturnOrderFulfillment.generated';
import {
  ContainerTypeAmount,
  OrderFulfillmentType,
  ContainerStationStatus,
} from '../../../graphql/generatedTypes';
import i18n from '@/utils/i18n';
import { Button, Collapse, Input, Table } from 'reactstrap';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { ContainerStationSearch } from '../../../components/ContainerStationSearch';
import OrderFulfillmentManualAmounts from './Components/OrderFulfillmentManualAmounts';
import { Communicator } from '../../../tools/Communication';
import { ContainerType } from './Components/OrderFulfillmentAddContainerTypes';
import { SelectItemLocation } from './Components/SelectItemLocation';
import {
  IsVytalEmployee,
  ViewerContext,
} from '../../../providers/ViewerContextProvider';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { useHistory, useParams } from 'react-router';
import { useCreateDeliveryOrderFulfillmentMutation } from '../../../graphql/mutations/createDeliveryOrderFulfillment.generated';
import convertDateToDateTimeLocalString from '@/utils/convertDateToDateTimeLocalString';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { OrderFulfillmentDetailFragment } from '../../../graphql/fragments/orderFulfillmentDetail.generated';
import TrackableItemDisplay from '../../../components/TrackableItem/TrackableItemDisplay';
import { Routes } from '@/utils/routes';
import ClassicLoadingSpinner from '../../../components/LoadingSpinner/ClassicLoadingSpinner';

type ContainerStation = Pick<
  OrderFulfillmentDetailFragment['container_stations'][number],
  'id' | 'name' | 'item_type' | 'containers'
>;

type Container = {
  id: string;
  type_id: number;
};

export const CreateOrderFulfillmentPage = () => {
  const viewer = useContext(ViewerContext);
  const { timestamp } = useParams<{ timestamp?: string }>();

  const history = useHistory();
  const isInternal = IsVytalEmployee(viewer);

  // Get only the allowed order fulfillment types
  const orderFulfillmentTypes = [
    OrderFulfillmentType.Delivery,
    OrderFulfillmentType.Return,
    OrderFulfillmentType.Picking,
  ];

  const [orderFulfillmentType, setOrderFulfillmentType] =
    useState<OrderFulfillmentType>(OrderFulfillmentType.Delivery);
  const [creationDate, setCreationDate] = useState(new Date());
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [eventId, setEventId] = useState<string | null>(null);
  const [eventAccountId, setEventAccountId] = useState<string | null>(null);
  const [itemLocationId, setItemLocationId] = useState<string | null>(null);
  const [containerStations, setContainerStations] = useState<
    ContainerStation[]
  >([]);
  const [manualAmount, setManualAmount] = useState<ContainerTypeAmount[]>([]);
  const [destinationEventId, setDestinationEventId] = useState<string | null>(
    null,
  );
  const [destinationEventAccountId, setDestinationEventAccountId] = useState<
    string | null
  >(null);
  const [destinationLocationId, setDestinationLocationId] = useState<
    string | null
  >(null);
  const [itemLocationName, setItemLocationName] = useState<string | null>('');
  const [containers, setContainers] = useState<Container[]>([]);

  const [isContainerStationSelectionOpen, setIsContainerStationSelectionOpen] =
    useState(false);
  const [isAdvancedShown, setIsAdvancedShown] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    Communicator.Get('api/3/containers/getAllContainerTypes', {})
      .then((response) => response.json())
      .then((data) => {
        setContainerTypes(data);
      });
  }, []);

  useEffect(() => {
    if (timestamp != null) {
      const data = JSON.parse(
        localStorage.getItem('copied_order_fulfillment_' + timestamp) || '{}',
      );
      if (Object.keys(data).length > 0) {
        setEventId(data.event_id);
        setEventAccountId(data.event_account_id);
        setItemLocationId(data.item_location.id);
        setItemLocationName(data.item_location.name);
        setContainerStations(data.container_stations);
        setManualAmount(data.manual_amounts);
        setContainers(data.single_containers);
        // Map the stored type to one of the allowed OrderFulfillmentType enum values
        const storedType = data.type.toUpperCase();
        const validType = orderFulfillmentTypes.find(
          (type) => type === storedType,
        );
        setOrderFulfillmentType(validType || OrderFulfillmentType.Delivery);
      }
      localStorage.removeItem('copied_order_fulfillment_' + timestamp);
    }
  }, [timestamp]);

  const [createReturnOrderFulfillment] =
    useCreateReturnOrderFulfillmentMutation();

  const [createDeliveryOrderFulfillment] =
    useCreateDeliveryOrderFulfillmentMutation();

  function toggleContainerStation(station: ContainerStation) {
    if (containerStations.some((s) => s.id === station.id)) {
      setContainerStations(
        containerStations.filter((s) => s.id !== station.id),
      );
    } else {
      setContainerStations([...containerStations, station]);
    }
  }

  const isCreateDisabled =
    eventId == null ||
    itemLocationId == null ||
    eventAccountId == null ||
    isCreating;

  async function handleCreateOrderFulfillment() {
    // Validate required fields
    if (isCreateDisabled) {
      return;
    }

    // Additional validation
    if (!eventId || !eventAccountId || !itemLocationId) {
      LayoutStoreInstance.setError(
        i18n.t('CreateOrderFulfillment:missingRequiredFields'),
        i18n.t('Common:error'),
      );
      return;
    }

    setIsCreating(true);

    try {
      if (orderFulfillmentType === OrderFulfillmentType.Return) {
        // For return type, check if destination location is provided when needed
        if (!destinationLocationId) {
          LayoutStoreInstance.setError(
            i18n.t('CreateOrderFulfillment:destinationLocationRequired'),
            i18n.t('Common:error'),
          );
          setIsCreating(false);
          return;
        }

        const res = await createReturnOrderFulfillment({
          variables: {
            payload: {
              event_id: eventId,
              event_account_id: eventAccountId,
              source_item_location_id: itemLocationId,
              target_item_location_id: destinationLocationId,
              container_stations: containerStations.map((station) => ({
                container_station_id: station.id,
                manual_amounts: [],
                status: ContainerStationStatus.Counted,
              })),
              manual_amounts: manualAmount.map((ma) => ({
                container_type_id: ma.container_type_id,
                amount: parseInt(ma.amount as any) || 0,
                amount_sealed: parseInt(ma.amount_sealed as any) || 0,
              })),
              timestamp: creationDate.toISOString(),
            },
          },
        });

        if (res.data?.createReturnOrderFulfillmentV2?.id) {
          LayoutStoreInstance.setSuccess(
            i18n.t('CreateOrderFulfillment:successCreated'),
          );
          history.push(
            Routes.orderFulfillment(res.data.createReturnOrderFulfillmentV2.id),
          );
        } else {
          LayoutStoreInstance.setError(
            i18n.t('CreateOrderFulfillment:errorCreating'),
            i18n.t('Common:error'),
          );
        }
      } else if (
        orderFulfillmentType === OrderFulfillmentType.Delivery ||
        orderFulfillmentType === OrderFulfillmentType.Picking
      ) {
        const res = await createDeliveryOrderFulfillment({
          variables: {
            event_id: eventId,
            event_account_id: eventAccountId,
            item_location_id: itemLocationId,
            container_ids: containers.map((c) => c.id),
            container_station_ids: containerStations.map((s) => s.id),
            manual_amounts: manualAmount.map((ma) => ({
              container_type_id: ma.container_type_id,
              amount: parseInt(ma.amount as any) || 0,
            })),
            timestamp: creationDate.toISOString(),
            type_override:
              orderFulfillmentType === OrderFulfillmentType.Picking
                ? OrderFulfillmentType.Picking
                : undefined,
          },
        });

        if (res.data?.createDeliveryOrderFulfillment?.id) {
          LayoutStoreInstance.setSuccess(
            i18n.t('CreateOrderFulfillment:successCreated'),
          );
          history.push(
            Routes.orderFulfillment(res.data.createDeliveryOrderFulfillment.id),
          );
        } else {
          LayoutStoreInstance.setError(
            i18n.t('CreateOrderFulfillment:errorCreating'),
            i18n.t('Common:error'),
          );
        }
      } else {
        // Fallback for any other types
        const typeTranslationKey =
          `OrderFulfillmentType:${orderFulfillmentType?.toUpperCase()}` as any;
        LayoutStoreInstance.setError(
          `Creating ${i18n.t(
            typeTranslationKey,
          )} order fulfillments is not yet implemented. Please contact the development team to add support for this order fulfillment type.`,
          'Not Yet Implemented',
        );
      }
    } catch (error: any) {
      console.error('Error creating order fulfillment:', error);

      // Handle different types of errors
      let errorMessage = i18n.t('CreateOrderFulfillment:errorCreating');

      if (error?.networkError) {
        errorMessage = i18n.t('CreateOrderFulfillment:networkError');
      } else if (error?.graphQLErrors?.length > 0) {
        // Use the first GraphQL error message if available
        errorMessage = error.graphQLErrors[0].message || errorMessage;
      } else if (error?.message) {
        // Generic error message
        errorMessage = error.message;
      }

      LayoutStoreInstance.setError(errorMessage, i18n.t('Common:error'));
    } finally {
      setIsCreating(false);
    }
  }

  function getContainersTable(containers: Container[]) {
    const containersAmountsByTpe = containers.reduce(
      (acc, container) => {
        if (acc[container.type_id]) {
          acc[container.type_id] += 1;
        } else {
          acc[container.type_id] = 1;
        }
        return acc;
      },
      {} as Record<number, number>,
    );

    const containerAmountsWithType = Object.entries(containersAmountsByTpe)
      .map(([typeId, amount]) => {
        const type = containerTypes.find((t) => t.id === parseInt(typeId));
        return {
          type: type || {
            id: parseInt(typeId),
            name: 'Unknown',
            hexCode: '',
            imageUrl: DEFAULT_CONTAINER_TYPE_ICON,
          },
          amount,
        };
      })
      .sort((a, b) => a.type.id - b.type.id);

    return containerAmountsWithType.map((containerAmountWithType) => (
      <Table>
        <thead>
          <tr>
            <th>{i18n.t('Common:name')}</th>
            <th>{i18n.t('Common:amount')}</th>
          </tr>
        </thead>
        <tbody>
          <tr key={containerAmountWithType.type.id}>
            <td>
              <div
                className="rounded-circle float-start me-2"
                style={{
                  backgroundColor:
                    '#' + (containerAmountWithType.type.hexCode || '000000'),
                  width: '3rem',
                  height: '3rem',
                }}
              ></div>
              <img
                className="flat-start me-2"
                alt={'Container type image ' + containerAmountWithType.type.id}
                src={
                  containerAmountWithType.type.imageUrl ||
                  DEFAULT_CONTAINER_TYPE_ICON
                }
                style={{ width: '3rem', height: '3rem' }}
              ></img>{' '}
              {containerAmountWithType.type.name}
            </td>
            <td>{containerAmountWithType.amount}</td>
          </tr>
        </tbody>
      </Table>
    ));
  }

  return (
    <>
      <ContentSection
        title={i18n.t('CreateOrderFulfillment:createOrderFulfillment')}
        collapsable={false}
      >
        <>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('CreateOrderFulfillment:type')}
            </span>
            <Input
              type="select"
              className="form-control"
              value={orderFulfillmentType}
              onChange={(e) =>
                setOrderFulfillmentType(e.target.value as OrderFulfillmentType)
              }
            >
              {orderFulfillmentTypes.map((type) => (
                <option key={type} value={type}>
                  {i18n.t(`OrderFulfillmentType:${type}`)}
                </option>
              ))}
            </Input>
          </div>
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:dateTime')}
            </span>
            <Input
              type="datetime-local"
              className="form-control"
              value={convertDateToDateTimeLocalString(creationDate)}
              onChange={(e) => setCreationDate(new Date(e.currentTarget.value))}
            />
          </div>
        </>
      </ContentSection>

      <SelectItemLocation
        eventId={eventId}
        eventAccountId={eventAccountId}
        itemLocationId={itemLocationId}
        itemLocationName={itemLocationName}
        setItemLocationId={setItemLocationId}
        setEventAccountId={setEventAccountId}
        setEventId={setEventId}
        mustSelectEvent={true}
        selectEventTitle={i18n.t('CreateOrderFulfillment:selectEvent')}
        selectEventAccountTitle={i18n.t(
          'CreateOrderFulfillment:selectEventAccount',
        )}
        selectItemLocationTitle={i18n.t(
          'CreateOrderFulfillment:selectItemLocation',
        )}
      />

      <ContentSection
        title={i18n.t('CreateOrderFulfillment:selectManualAmounts')}
        collapsable={false}
      >
        <p>
          {orderFulfillmentType === OrderFulfillmentType.Delivery &&
            i18n.t('OrderFulfillment:manualAmountsDeliveryText')}
          {orderFulfillmentType === OrderFulfillmentType.Return &&
            i18n.t('OrderFulfillment:manualAmountsReturnText')}
        </p>
        <OrderFulfillmentManualAmounts
          manualAmounts={manualAmount}
          containerTypes={containerTypes}
          isInternal={isInternal}
          setManualAmounts={setManualAmount}
          editModeAlwaysOn={true}
          type={orderFulfillmentType}
        />
      </ContentSection>

      {/** Advances options */}

      {isAdvancedShown && (
        <ContentSection
          title={i18n.t('CreateOrderFulfillment:advancedOptions')}
          collapsable={false}
        >
          <ContentSection
            title={i18n.t('CreateOrderFulfillment:selectContainerStation')}
            collapsable={false}
          >
            <Button
              className="float-end mt-2 mb-2"
              onClick={() =>
                setIsContainerStationSelectionOpen(
                  !isContainerStationSelectionOpen,
                )
              }
            >
              {isContainerStationSelectionOpen ? (
                <FaChevronDown />
              ) : (
                <FaChevronRight />
              )}
            </Button>

            <Collapse isOpen={isContainerStationSelectionOpen}>
              <h3 className="mt-4 mb-4">
                {i18n.t('CreateOrderFulfillment:selection')}
              </h3>
              <ContainerStationSearch
                onClick={(station) => {
                  toggleContainerStation({ ...station, containers: [] } as any);
                }}
                showAddButton
              />
            </Collapse>

            <h3 className="mt-4 mb-4">
              {i18n.t('CreateOrderFulfillment:selected')}
            </h3>
            <Table>
              <thead>
                <tr>
                  <th></th>
                  <th>{i18n.t('Common:name')}</th>
                  <th>{i18n.t('CreateOrderFulfillment:containers')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {containerStations.length === 0 && (
                  <tr>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </tr>
                )}
                {containerStations.map((station) => (
                  <tr key={station.id}>
                    <td>
                      <TrackableItemDisplay
                        trackableItem={station}
                        showTrackableItemId={false}
                        showLink={false}
                        showName={false}
                      />
                    </td>
                    <td>{station.name}</td>
                    <td>
                      {getContainersTable(
                        station.containers.map((c) => ({
                          id: c.container_id,
                          type_id: c.container_type_id,
                        })),
                      )}
                    </td>
                    <td>
                      <Button
                        color="danger"
                        onClick={() => toggleContainerStation(station)}
                      >
                        {i18n.t('Common:remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </ContentSection>
          {containers.length > 0 && (
            <ContentSection
              title={i18n.t('CreateOrderFulfillment:singleContainers')}
              collapsable={false}
            >
              {getContainersTable(containers)}
            </ContentSection>
          )}
          {orderFulfillmentType === OrderFulfillmentType.Return && (
            <SelectItemLocation
              itemLocationName={null}
              eventId={destinationEventId}
              eventAccountId={destinationEventAccountId}
              itemLocationId={destinationLocationId}
              setItemLocationId={setDestinationLocationId}
              setEventAccountId={setDestinationEventAccountId}
              setEventId={setDestinationEventId}
              mustSelectEvent={true}
              selectEventTitle={i18n.t(
                'CreateOrderFulfillment:selectDestinationEvent',
              )}
              selectEventAccountTitle={i18n.t(
                'CreateOrderFulfillment:selectDestinationEventAccount',
              )}
              selectItemLocationTitle={i18n.t(
                'CreateOrderFulfillment:selectDestinationItemLocation',
              )}
            />
          )}
        </ContentSection>
      )}

      <div className="d-flex justify-content-between">
        <Button
          color={isAdvancedShown ? 'primary' : 'secondary'}
          className="mt-2 mb-2"
          onClick={() => {
            setIsAdvancedShown(!isAdvancedShown);
          }}
        >
          {i18n.t(
            isAdvancedShown
              ? 'CreateOrderFulfillment:hideAdvancedOptions'
              : 'CreateOrderFulfillment:showAdvancedOptions',
          )}
        </Button>
        <Button
          disabled={isCreateDisabled}
          color="success"
          className="mt-2 mb-2"
          onClick={handleCreateOrderFulfillment}
        >
          {isCreating ? (
            <ClassicLoadingSpinner />
          ) : (
            i18n.t('CreateOrderFulfillment:createOrderFulfillment')
          )}
        </Button>
      </div>
    </>
  );
};
